import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductManagement({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ 
    name: "", category: "subscription", price: "", description: "", commission_override: "" 
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers });
      setProducts(res.data.products);
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const createProduct = async () => {
    if (!newProduct.name || !newProduct.price) { 
      toast.error("Fill required fields"); 
      return; 
    }
    try {
      await axios.post(`${API}/admin/products`, { 
        ...newProduct, 
        price: parseFloat(newProduct.price), 
        commission_override: newProduct.commission_override ? parseFloat(newProduct.commission_override) : null 
      }, { headers });
      toast.success("Product created");
      setNewProduct({ name: "", category: "subscription", price: "", description: "", commission_override: "" });
      setShowForm(false);
      fetchProducts();
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Failed"); 
    }
  };

  const toggleProduct = async (id, currentStatus) => {
    try {
      await axios.put(`${API}/admin/products/${id}`, { is_active: !currentStatus }, { headers });
      toast.success("Updated");
      fetchProducts();
    } catch { toast.error("Failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers });
      toast.success("Deleted");
      fetchProducts();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="product-management">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Add Product
        </Button>
      </div>

      {showForm && (
        <div className="card p-6 border border-[#D4AF37]/30" data-testid="product-form">
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4">New Product</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Name *</label>
              <Input 
                value={newProduct.name} 
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} 
                className="input-dark"
                data-testid="product-name"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Category</label>
              <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                <SelectTrigger className="input-dark" data-testid="product-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="device">Device</SelectItem>
                  <SelectItem value="addon">Add-on</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Price *</label>
              <Input 
                type="number" 
                value={newProduct.price} 
                onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} 
                className="input-dark"
                data-testid="product-price"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Commission Override ($)</label>
              <Input 
                type="number" 
                value={newProduct.commission_override} 
                onChange={(e) => setNewProduct({...newProduct, commission_override: e.target.value})} 
                placeholder="Default" 
                className="input-dark"
                data-testid="product-commission"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Description</label>
              <Input 
                value={newProduct.description} 
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} 
                className="input-dark"
                data-testid="product-description"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={createProduct} className="btn-primary" data-testid="create-product-btn">Create</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-white/70">Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="product-grid">
        {products.length === 0 ? (
          <div className="col-span-3 text-center text-white/50 py-12">No products yet. Add your first product.</div>
        ) : (
          products.map((product) => (
            <div 
              key={product.id} 
              className={`card p-6 border ${product.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}
              data-testid={`product-card-${product.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className={`text-xs px-2 py-0.5 ${
                    product.category === 'subscription' ? 'bg-purple-500/20 text-purple-400' : 
                    product.category === 'device' ? 'bg-blue-500/20 text-blue-400' : 
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {product.category}
                  </span>
                  <h4 className="font-semibold text-white mt-2">{product.name}</h4>
                </div>
                <span className="text-2xl font-bold text-[#D4AF37]">${product.price}</span>
              </div>
              {product.description && <p className="text-white/50 text-sm mb-4">{product.description}</p>}
              {product.commission_override && (
                <p className="text-[#00D4FF] text-xs mb-4">Commission: ${product.commission_override}</p>
              )}
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => toggleProduct(product.id, product.is_active)} 
                  className={product.is_active ? 'text-yellow-400' : 'text-green-400'}
                  data-testid={`toggle-product-${product.id}`}
                >
                  {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  <span className="ml-1">{product.is_active ? 'Disable' : 'Enable'}</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => deleteProduct(product.id)} 
                  className="text-red-400"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
